import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function TimeseriesViewer({ selectedDate, selectedCampaign }) {
  const [selectedTimeseriesPlot, setSelectedTimeseriesPlot] = useState(null);
  const [timeseriesPlotType, setTimeseriesPlotType] = useState(null); // Type of plot (html or png)
  const [plotType, setPlotType] = useState('backscatter'); // Default to backscatter
  const [availablePlotTypes, setAvailablePlotTypes] = useState([]);
  const [integrationTime, setIntegrationTime] = useState(null); // Selected integration time (e.g. '20MIN' or 'default')
  const [availableIntegrationTimes, setAvailableIntegrationTimes] = useState([]);

  // Detect if user is on mobile device
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           window.innerWidth <= 768;
  };

  // availablePlotTypes now comes with both value and label from the API

  // Fetch available plot types when date or campaign changes
  useEffect(() => {
    if (!selectedDate || !selectedCampaign) {
      setAvailablePlotTypes([]);
      return;
    }

    const formattedDate = format(selectedDate, 'yyyy-MM-dd');
    
    fetch(`${API_BASE}/api/timeseries-available/${selectedCampaign}/${formattedDate}`, {
      credentials: 'include' // Include session cookie
    })
      .then(res => res.ok ? res.json() : Promise.reject('Failed to fetch available plot types'))
      .then(data => {
        console.log('Available plot types:', data.availablePlotTypes);
        setAvailablePlotTypes(data.availablePlotTypes);
        
        // If current plotType is not available, switch to the first available one
        if (data.availablePlotTypes.length > 0 && !data.availablePlotTypes.some(p => p.value === plotType)) {
          setPlotType(data.availablePlotTypes[0].value);
        }
      })
      .catch(err => {
        console.error('Error fetching available plot types:', err);
        setAvailablePlotTypes([]);
      });
  }, [selectedDate, selectedCampaign]);

  // Fetch the available integration times whenever date, campaign, or plot type changes
  useEffect(() => {
    if (!selectedDate || !selectedCampaign || !plotType) {
      setAvailableIntegrationTimes([]);
      return;
    }

    const formattedDate = format(selectedDate, 'yyyy-MM-dd');

    fetch(`${API_BASE}/api/timeseries-times/${selectedCampaign}/${formattedDate}/${plotType}`, {
      credentials: 'include'
    })
      .then(res => res.ok ? res.json() : Promise.reject('Failed to fetch available integration times'))
      .then(data => {
        setAvailableIntegrationTimes(data.times);
        setIntegrationTime(prev => (data.times.some(t => t.value === prev) ? prev : (data.times[0]?.value ?? null)));
      })
      .catch(err => {
        console.error('Error fetching available integration times:', err);
        setAvailableIntegrationTimes([]);
      });
  }, [selectedDate, selectedCampaign, plotType]);

  // Fetch the timeseries plot for selected date, campaign, plot type, and integration time
  useEffect(() => {
    if (!selectedDate || !selectedCampaign) {
      setSelectedTimeseriesPlot(null);
      setTimeseriesPlotType(null);
      return;
    }

    const formattedDate = format(selectedDate, 'yyyy-MM-dd');
    const params = new URLSearchParams();
    if (isMobile()) params.set('mobile', 'true');
    if (integrationTime) params.set('integration', integrationTime);

    fetch(`${API_BASE}/api/timeseries/${selectedCampaign}/${formattedDate}/${plotType}?${params.toString()}`, {
      credentials: 'include' // Include session cookie
    })
      .then(res => res.ok ? res.json() : Promise.reject('Plot not found'))
      .then(data => {
        console.log('Timeseries plot data:', data);
        setSelectedTimeseriesPlot(`${API_BASE}${data.original}`);
        setTimeseriesPlotType(data.type);
      })
      .catch(err => {
        console.error('Error fetching timeseries plot:', err);
        setSelectedTimeseriesPlot(null);
        setTimeseriesPlotType(null);
      });
  }, [selectedDate, selectedCampaign, plotType, availablePlotTypes, integrationTime]);

  return (
    <div className="content-card">
      <h2>Timeseries Comparisons</h2>
      
      {(selectedCampaign === 'Test' || selectedCampaign === 'Kuehlungsborn' || selectedCampaign === 'Test_Andoya') && (
        <div className="warning-banner">
          ⚠️ Warning - currently with dummy lidar data!
        </div>
      )}
      
      <div>
        <div className="plot-controls">
          <h4 style={{ margin: 0 }}>
            Comparison of timeseries for {selectedCampaign} — {format(selectedDate, 'yyyy-MM-dd')}
          </h4>
          
          <select
            className="modern-select"
            value={plotType}
            onChange={(e) => setPlotType(e.target.value)}
            disabled={availablePlotTypes.length === 0}
          >
            {availablePlotTypes.length === 0 ? (
              <option value="">No timeseries available</option>
            ) : (
              availablePlotTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))
            )}
          </select>

          {availableIntegrationTimes.length > 1 && (
            <select
              className="modern-select"
              value={integrationTime || ''}
              onChange={(e) => setIntegrationTime(e.target.value)}
            >
              {availableIntegrationTimes.map(time => (
                <option key={time.value} value={time.value}>
                  {time.label}
                </option>
              ))}
            </select>
          )}
        </div>

        {selectedTimeseriesPlot ? (
          <div>
            {timeseriesPlotType === 'html' && (
              <p className="interactive-indicator">
                ✨ Interactive plot (zoomable)
              </p>
            )}
            <div className="plot-container" style={{ maxWidth: timeseriesPlotType === 'html' ? '100%' : '1000px' }}>
              {timeseriesPlotType === 'html' ? (
                <iframe 
                  src={selectedTimeseriesPlot} 
                  title={`${availablePlotTypes.find(p => p.value === plotType)?.label || plotType} timeseries`}
                  className="interactive-plot-iframe-timeseries"
                />
              ) : (
                <img 
                  src={selectedTimeseriesPlot} 
                  alt={`${availablePlotTypes.find(p => p.value === plotType)?.label || plotType} timeseries`}
                  style={{ width: '100%', height: 'auto', borderRadius: '8px' }} 
                />
              )}
            </div>
          </div>
        ) : (selectedDate && selectedCampaign) ? (
          availablePlotTypes.length === 0 ? (
            <div className="empty-state">
              <p>No timeseries plots available for this date and campaign.</p>
            </div>
          ) : (
            <div className="loading-state">
              <span className="loading-spinner"></span>
              Loading timeseries plot...
            </div>
          )
        ) : !selectedCampaign ? (
          <div className="empty-state">
            <p>Please select a campaign site.</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default TimeseriesViewer;